import dayjs from 'dayjs';
import { AuctionRequestsType } from '../actions/auction';
import { BiddingViewAction } from '../actions/objectViewDrawerActions';
import {
  auctionClosed,
  auctionCloseTimeUpdated,
  auctionCountdownStarted,
  auctionFavouriteCountUpdated
} from '../actions/connection-events';
import {
  RemoveFavouriteAuctionAction,
  StoreFavouriteAuctionAction
} from '../actions/favouriteAuctions';
import Auction, { AuctionNotFound } from '../interfaces/Auction';
import { ReduxStore } from '../interfaces/store';
import { ObjectView } from '../interfaces/store/ObjectView';
import { unionBy } from '~/helpers/unionBy';

function isAuctionNotFound(
  auction: Auction | AuctionNotFound
): auction is AuctionNotFound {
  return !(auction as Auction)?.id;
}

const initialState: ObjectView = {
  storeObjects: {},
  isFetching: false,
  auctionId: undefined,
  isOpen: false,
  buyMethod: null,
  bidAmount: null,
  isLoading: false,
  allBids: [],
  flow: 'default',
  supportSchedule: {
    schedule: [],
    lastUpdate: null
  }
};

type ObjectViewAction =
  | AuctionRequestsType
  | BiddingViewAction
  | ReturnType<typeof auctionCountdownStarted>
  | ReturnType<typeof auctionClosed>
  | ReturnType<typeof auctionCloseTimeUpdated>
  | ReturnType<typeof auctionFavouriteCountUpdated>
  | StoreFavouriteAuctionAction
  | RemoveFavouriteAuctionAction;

const withFavouriteCount = (
  state: ObjectView,
  auctionId: string,
  getCount: (favouriteCount: number) => number
): ObjectView => {
  const auction = state.storeObjects[auctionId];

  if (!auction) {
    return state;
  }

  return {
    ...state,
    storeObjects: {
      ...state.storeObjects,
      [auctionId]: {
        ...auction,
        favouriteCount: getCount(auction.favouriteCount)
      }
    }
  };
};

export const objectView = (
  state: ObjectView = initialState,
  action: ObjectViewAction
): ReduxStore['objectView'] => {
  switch (action.type) {
    case 'GET_AUCTION_REQUEST':
      return {
        ...state,
        isFetching: true
      };

    case 'GET_AUCTION_SUCCESS': {
      // if auction does not exist, the backend will response with AuctionNotFound schema
      if (isAuctionNotFound(action?.payload)) {
        return { ...state, isFetching: false };
      }

      return {
        ...state,
        storeObjects: {
          ...state.storeObjects,
          [action.payload.id]: action.payload
        },
        isFetching: false
      };
    }

    case 'GET_AUCTION_FAILURE': {
      return {
        ...state,
        isFetching: false,
        storeObjects: {
          ...state.storeObjects,
          [action.payload]: undefined
        }
      };
    }

    case 'OPEN_BIDDING_VIEW':
      return Object.assign({}, state, {
        isOpen: true,
        auctionId: action.payload,
        flow: action.flow,
        buyMethod: action.buyMethod
      });
    case 'CLOSE_BIDDING_VIEW':
      return Object.assign({}, state, {
        isOpen: false,
        bidAmount: null,
        buyMethod: null
      });

    case 'BIDDING_VIEW_SET_SUPPORT_SCHEDULE':
      return {
        ...state,
        supportSchedule: {
          schedule: action.payload ?? [],
          lastUpdate: action.payload ? dayjs() : null
        }
      };

    case 'BIDDING_VIEW_SET_ALL_BIDS': {
      if (!action.payload) {
        return state;
      }

      const bids = action.payload.map(bid => ({
        ...bid,
        auctionId: String(bid.auctionId)
      }));

      const auctionId = bids[0]?.auctionId;

      if (auctionId && state.auctionId === auctionId) {
        return { ...state, allBids: bids };
      }

      return state;
    }

    case 'AUCTION_COUNTDOWN_STARTED': {
      const { auctionId, countdownDuration } = action.payload;
      const auction = state.storeObjects[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      return {
        ...state,
        storeObjects: {
          ...state.storeObjects,
          [auctionId]: {
            ...auction,
            countdownDuration,
            activeAuction: {
              ...auction.activeAuction,
              countdownInProgress: true
            }
          }
        }
      };
    }

    case 'AUCTION_CLOSED': {
      const { auctionId, highestBid } = action.payload;
      const auction = state.storeObjects[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      const prevBids = auction.activeAuction?.bids ?? [];
      const newBids = unionBy([...prevBids, highestBid], 'id');

      return {
        ...state,
        storeObjects: {
          ...state.storeObjects,
          [auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              auctionId,
              closed: true,
              countdownInProgress: false,
              highestBid,
              bids: newBids
            }
          }
        }
      };
    }

    case 'AUCTION_CLOSE_TIME_UPDATED': {
      const { auctionId, preliminaryCloseAt } = action.payload;
      const auction = state.storeObjects[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      return {
        ...state,
        storeObjects: {
          ...state.storeObjects,
          [auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              preliminaryCloseAt
            }
          }
        }
      };
    }

    case 'AUCTION_FAVOURITE_COUNT_UPDATED':
      return withFavouriteCount(
        state,
        action.payload.auctionId,
        () => action.payload.favouriteCount
      );

    case 'STORE_FAVOURITE_AUCTION_REQUEST':
    case 'REMOVE_FAVOURITE_AUCTION_FAILURE':
      return withFavouriteCount(
        state,
        action.payload,
        favouriteCount => favouriteCount + 1
      );

    case 'REMOVE_FAVOURITE_AUCTION_REQUEST':
    case 'STORE_FAVOURITE_AUCTION_FAILURE':
      return withFavouriteCount(state, action.payload, favouriteCount =>
        Math.max(0, favouriteCount - 1)
      );

    default:
      return { ...state };
  }
};
